/**
 * Settings → Nightshift → Routes: every Machine → Harness → Model → Thinking route in the
 * product on one page, and a way to change a model everywhere at once.
 *
 *   - The summary counts each model's routes, stored and inherited, across projects and tickets,
 *     and marks retired, unknown and switched-off models.
 *   - The table lists every route grouped by owner, filterable by project, stage, harness, model
 *     and machine.
 *   - Change routes replaces one harness + model with another from the live catalogue (the same
 *     pickers as every route list), or resets projects to inherit. It always previews a
 *     before/after diff first, and applies only that preview: if anything moved in between, the
 *     server refuses and the fresh preview is shown instead.
 *
 * Admin only, reads included, because the inventory spans every project.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UiCatalogueModel, UiRouteChange, UiRouteEntry, UiRouteInventory, UiRoutePlan, UiRouteRow } from '@nightshift/ui-types';

import { Empty, Kicker, Since, Spinner } from '../components/primitives.tsx';
import { HarnessPicker, ModelPicker, ThinkingPicker, type RouteMachine, type RouteRow } from '../components/route-list-editor.tsx';
import { canWriteSettings } from '../lib/privilege.ts';
import { harnessLabel } from '../lib/route-list.ts';
import {
  ANY_MACHINE,
  EMPTY_ROUTES_FILTER,
  filterActive,
  filterOptions,
  filterRoutes,
  groupNote,
  groupRoutes,
  replaceSources,
  routeStatus,
  routeText,
  storageLabel,
  usageSentence,
  type RoutesFilter,
} from '../lib/routes-overview.ts';
import { ApiError, api, type SessionUser } from '../live/api.ts';
import './routes-settings.css';

export function RoutesSettingsPanel({ user }: { user: SessionUser | null }) {
  const [inventory, setInventory] = useState<UiRouteInventory | null>(null);
  const [error, setError] = useState<{ message: string; status: number | null } | null>(null);
  const [filter, setFilter] = useState<RoutesFilter>(EMPTY_ROUTES_FILTER);
  const [machines, setMachines] = useState<RouteMachine[]>([]);
  const [models, setModels] = useState<UiCatalogueModel[]>([]);
  const [replaceFrom, setReplaceFrom] = useState<string>('');
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      setInventory(await api.routeInventory());
      setNow(Date.now());
      setError(null);
    } catch (caught) {
      setError({ message: caught instanceof Error ? caught.message : String(caught), status: caught instanceof ApiError ? caught.status : null });
    }
  }, []);

  const admin = user !== null && canWriteSettings(user);
  useEffect(() => {
    if (admin) void load();
  }, [admin, load]);

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    void Promise.all([api.machines().catch(() => null), api.modelsCatalogue(null).catch(() => null)]).then(([hosts, catalogue]) => {
      if (cancelled) return;
      setMachines((hosts?.items ?? []).map((item) => ({ name: item.name, installedHarnesses: item.installedHarnesses, live: item.live, paused: item.paused })));
      setModels(catalogue?.models ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [admin]);

  if (!admin) {
    return <Empty title="Only an admin can see every route." body="The routes overview spans every project, so it is limited to admins. Project routes are under Project → Execution." />;
  }
  if (error && !inventory) {
    return (
      <Empty
        title="The routes could not be read."
        body={error.status === 403 ? 'This session is not an admin.' : error.message}
        action={<button type="button" className="btn" onClick={() => void load()}>Try again</button>}
      />
    );
  }
  if (!inventory) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="cfg-stack routes-overview">
      <p className="sheet-note cfg-provenance">
        Every route the factory stores or inherits: the Nightshift defaults, each project’s execution
        and sidecar lists, night-manager defaults and overrides on open tickets. Inherited rows show
        what a project runs because it stores nothing of its own; change those at their source.
      </p>
      <ModelSummary
        inventory={inventory}
        onFilter={(usage) => setFilter({ ...EMPTY_ROUTES_FILTER, harness: usage.harness, model: usage.model })}
        onReplace={(key) => setReplaceFrom(key)}
      />
      <ChangeRoutes
        key={replaceFrom}
        inventory={inventory}
        machines={machines}
        models={models}
        initialFrom={replaceFrom}
        onApplied={() => void load()}
      />
      <RoutesTable inventory={inventory} filter={filter} onFilter={setFilter} />
      <RecentChanges inventory={inventory} now={now} />
    </div>
  );
}

/* ── summary ─────────────────────────────────────────────────────────────── */

function StatusChip({ entry }: { entry: Pick<UiRouteEntry, 'catalogue' | 'enabled' | 'problems'> }) {
  const status = routeStatus(entry);
  return (
    <span className="chip routes-status" data-tone={status.tone} title={entry.problems.join('\n') || undefined}>
      <span className="lamp" aria-hidden />
      {status.label}
    </span>
  );
}

function ModelSummary({
  inventory,
  onFilter,
  onReplace,
}: {
  inventory: UiRouteInventory;
  onFilter: (usage: UiRouteInventory['models'][number]) => void;
  onReplace: (key: string) => void;
}) {
  const replaceable = new Set(replaceSources(inventory).map((source) => source.key));
  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker count={inventory.models.length}>Models in use</Kicker>
        <span className="cfg-help" style={{ marginLeft: 'auto' }}>
          {inventory.entries.length} routes · {inventory.entries.filter((entry) => entry.explicit).length} stored ·{' '}
          {inventory.entries.filter((entry) => entry.problems.length > 0).length} flagged
        </span>
      </header>
      <div className="sheet-panel-body routes-scroll">
        <table className="table routes-summary">
          <thead>
            <tr>
              <th>Model</th>
              <th>Harness</th>
              <th>Catalogue</th>
              <th className="right">Routes</th>
              <th className="right">Stored</th>
              <th className="right">Projects</th>
              <th className="right">Tickets</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {inventory.models.map((usage) => {
              const key = `${usage.harness} ${usage.model}`;
              const tone = usage.catalogue === 'retired' ? 'danger' : usage.catalogue === 'unknown' ? 'warn' : undefined;
              return (
                <tr key={key} data-tone={tone}>
                  <td>
                    <div className="mono">{usage.model}</div>
                    <div className="cfg-help">{usageSentence(usage)}</div>
                  </td>
                  <td>{harnessLabel(usage.harness)}</td>
                  <td>
                    <StatusChip entry={{ catalogue: usage.catalogue, enabled: true, problems: [] }} />
                  </td>
                  <td className="right">{usage.routes}</td>
                  <td className="right">{usage.explicit}</td>
                  <td className="right">{usage.projects}</td>
                  <td className="right">{usage.tickets}</td>
                  <td>
                    <div className="routes-actions">
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => onFilter(usage)}>
                        Show routes
                      </button>
                      {replaceable.has(key) ? (
                        <button type="button" className="btn btn-sm" onClick={() => onReplace(key)}>
                          Replace…
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── change routes ───────────────────────────────────────────────────────── */

const ANY_THINKING = '*';

function toggle<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** An ordered list in a diff cell: numbered, because the order is the fallback order. */
function RouteList({ rows }: { rows: readonly UiRouteRow[] }) {
  if (rows.length === 0) return <span className="dim">inherits</span>;
  return (
    <ol className="routes-ordered">
      {rows.map((row, index) => (
        <li key={index}>{routeText(row)}</li>
      ))}
    </ol>
  );
}

function ChangeRoutes({
  inventory,
  machines,
  models,
  initialFrom,
  onApplied,
}: {
  inventory: UiRouteInventory;
  machines: readonly RouteMachine[];
  models: readonly UiCatalogueModel[];
  initialFrom: string;
  onApplied: () => void;
}) {
  const sources = useMemo(() => replaceSources(inventory), [inventory]);
  const [mode, setMode] = useState<'replace' | 'reset'>('replace');
  const [fromKey, setFromKey] = useState(initialFrom || sources[0]?.key || '');
  const [fromThinking, setFromThinking] = useState(ANY_THINKING);
  const [target, setTarget] = useState<RouteRow | null>(null);
  const [keepThinking, setKeepThinking] = useState(true);
  const [projects, setProjects] = useState<number[]>([]);
  const [nightshift, setNightshift] = useState(true);
  const [slots, setSlots] = useState<string[]>([]);
  const [tickets, setTickets] = useState(false);
  const [plan, setPlan] = useState<UiRoutePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);

  const source = sources.find((entry) => entry.key === fromKey);
  // The target starts on the source's harness, on the first current model that is not the source.
  const chosen: RouteRow | null = useMemo(() => {
    if (target) return target;
    const harness = source?.harness ?? models[0]?.harness;
    const pick = models.find((model) => model.harness === harness && model.id !== source?.model) ?? models[0];
    return pick ? { machine: null, harness: pick.harness, model: pick.id, thinking: pick.defaultEffort } : null;
  }, [target, source, models]);

  const change: UiRouteChange | null = useMemo(() => {
    const selection = {
      ...(projects.length > 0 ? { projects } : {}),
      ...(slots.length > 0 ? { slots } : {}),
      nightshift: mode === 'replace' ? nightshift : false,
      tickets,
    };
    if (mode === 'reset') return { kind: 'reset', selection };
    if (!source || !chosen) return null;
    return {
      kind: 'replace',
      from: { harness: source.harness, model: source.model, ...(fromThinking === ANY_THINKING ? {} : { effort: fromThinking || null }) },
      to: { harness: chosen.harness, model: chosen.model, ...(keepThinking ? {} : { effort: chosen.thinking }) },
      selection,
    };
  }, [mode, source, chosen, fromThinking, keepThinking, projects, slots, nightshift, tickets]);

  // Any edit makes the preview stale; it has to be taken again before it can be applied.
  const edit = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPlan(null);
    setMessage(null);
  };

  const preview = async () => {
    if (!change) return;
    setBusy(true);
    setMessage(null);
    try {
      setPlan((await api.planRoutes(change)).plan);
    } catch (caught) {
      setPlan(null);
      setMessage({ tone: 'danger', text: caught instanceof Error ? caught.message : String(caught) });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!change || !plan) return;
    setBusy(true);
    try {
      const result = await api.applyRoutes(change, plan.token);
      if (result.ok) {
        setPlan(null);
        setMessage({ tone: 'ok', text: `Applied to ${result.changed} ${result.changed === 1 ? 'owner' : 'owners'}. Each change is in the audit below.` });
        onApplied();
      } else if (result.conflict) {
        setPlan(result.plan);
        setMessage({ tone: 'warn', text: `${result.message} Nothing was written.` });
      } else {
        setMessage({ tone: 'danger', text: `${result.message} Nothing was written.` });
      }
    } catch (caught) {
      setMessage({ tone: 'danger', text: caught instanceof Error ? caught.message : String(caught) });
    } finally {
      setBusy(false);
    }
  };

  // Only lists something is stored in: a registered list nobody uses yet is not a choice.
  const slotChoices = inventory.slots.filter(
    (slot) =>
      slot.scope !== 'ticket' &&
      (mode === 'replace' || (slot.resettable && slot.scope === 'project')) &&
      inventory.entries.some((entry) => entry.slot === slot.id && entry.editable),
  );

  return (
    <section className="sheet-panel routes-change">
      <header className="panel-head">
        <Kicker>Change routes</Kicker>
        <div className="routes-mode" role="radiogroup" aria-label="Kind of change" style={{ marginLeft: 'auto' }}>
          {(['replace', 'reset'] as const).map((kind) => (
            <label key={kind} className="routes-check">
              <input type="radio" name="routes-mode" checked={mode === kind} onChange={() => edit(setMode)(kind)} />
              {kind === 'replace' ? 'Replace a model' : 'Reset to inherit'}
            </label>
          ))}
        </div>
      </header>
      <div className="sheet-panel-body cfg-stack">
        {mode === 'replace' ? (
          <div className="routes-replace">
            <div className="routes-field">
              <span className="cfg-label">Replace</span>
              <div className="routes-inline">
                <select className="input mono" aria-label="Replace model" value={fromKey} onChange={(event) => { edit(setFromKey)(event.target.value); setFromThinking(ANY_THINKING); }}>
                  {sources.map((entry) => (
                    <option key={entry.key} value={entry.key}>
                      {harnessLabel(entry.harness)} · {entry.model} ({entry.usage.routes})
                    </option>
                  ))}
                </select>
                <select className="input" aria-label="Replace thinking" value={fromThinking} onChange={(event) => edit(setFromThinking)(event.target.value)}>
                  <option value={ANY_THINKING}>any thinking</option>
                  {(source?.efforts ?? []).map((effort) => (
                    <option key={effort ?? ''} value={effort ?? ''}>{effort ?? 'none'}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="routes-field">
              <span className="cfg-label">With</span>
              {chosen ? (
                <div className="routes-inline">
                  <HarnessPicker row={chosen} onChange={edit(setTarget)} machines={machines} models={models} label="Replacement harness" />
                  <ModelPicker row={chosen} onChange={edit(setTarget)} machines={machines} models={models} label="Replacement model" />
                  <ThinkingPicker row={chosen} onChange={edit(setTarget)} machines={machines} models={models} label="Replacement thinking" disabled={keepThinking} />
                  <label className="routes-check">
                    <input type="checkbox" checked={keepThinking} onChange={(event) => edit(setKeepThinking)(event.target.checked)} />
                    Keep each route’s thinking where offered
                  </label>
                </div>
              ) : (
                <p className="cfg-help">The live catalogue has no models to offer yet.</p>
              )}
              <p className="cfg-help">Choices come from the live catalogue. Each route keeps its machine and its place in the list.</p>
            </div>
          </div>
        ) : (
          <p className="cfg-help">
            Removes the selected projects’ own lists so they inherit the Nightshift defaults again. A
            project whose routes come from its repository file gets a Nightshift-managed copy without
            them; nothing is written to the repository.
          </p>
        )}

        <div className="routes-scope">
          <fieldset className="routes-field">
            <legend className="cfg-label">Projects</legend>
            <div className="routes-checks">
              {inventory.projects.map((project) => (
                <label key={project.id} className="routes-check">
                  <input type="checkbox" checked={projects.includes(project.id)} onChange={() => edit(setProjects)(toggle(projects, project.id))} />
                  {project.name}
                </label>
              ))}
            </div>
            <span className="cfg-help">{projects.length === 0 ? 'None ticked: every project.' : `${projects.length} ticked.`}</span>
          </fieldset>
          <fieldset className="routes-field">
            <legend className="cfg-label">Lists</legend>
            <div className="routes-checks">
              {slotChoices.map((slot) => (
                <label key={slot.id} className="routes-check">
                  <input type="checkbox" checked={slots.includes(slot.id)} onChange={() => edit(setSlots)(toggle(slots, slot.id))} />
                  {slot.label}
                </label>
              ))}
            </div>
            <span className="cfg-help">{slots.length === 0 ? 'None ticked: every list.' : `${slots.length} ticked.`}</span>
          </fieldset>
          <fieldset className="routes-field">
            <legend className="cfg-label">Also</legend>
            <div className="routes-checks">
              {mode === 'replace' ? (
                <label className="routes-check">
                  <input type="checkbox" checked={nightshift} onChange={(event) => edit(setNightshift)(event.target.checked)} />
                  Nightshift-wide lists
                </label>
              ) : null}
              <label className="routes-check">
                <input type="checkbox" checked={tickets} onChange={(event) => edit(setTickets)(event.target.checked)} />
                Overrides on open tickets
              </label>
            </div>
            <span className="cfg-help">Ticket overrides are left alone unless ticked.</span>
          </fieldset>
        </div>

        <div className="route-list-foot">
          <button type="button" className="btn" disabled={busy || !change} onClick={() => void preview()}>
            {busy && !plan ? 'Previewing…' : 'Preview'}
          </button>
          {plan ? (
            <button type="button" className="btn btn-accent" disabled={busy || plan.changed === 0} onClick={() => void apply()}>
              {busy ? 'Applying…' : `Apply to ${plan.changed} ${plan.changed === 1 ? 'owner' : 'owners'}`}
            </button>
          ) : null}
          {message ? (
            <span className="routes-message" data-tone={message.tone} role={message.tone === 'ok' ? 'status' : 'alert'}>
              {message.text}
            </span>
          ) : null}
        </div>
        {plan ? <PlanView plan={plan} /> : null}
      </div>
    </section>
  );
}

function PlanView({ plan }: { plan: UiRoutePlan }) {
  return (
    <div className="routes-plan" aria-label="Preview">
      <p className="cfg-help">
        {plan.changed === 0 && plan.blocked === 0
          ? 'Nothing stored matches. Nothing would change.'
          : `${plan.changed} ${plan.changed === 1 ? 'owner changes' : 'owners change'}, ${plan.routesChanged} ${plan.routesChanged === 1 ? 'route' : 'routes'} in all${plan.blocked > 0 ? `; ${plan.blocked} skipped` : ''}.`}
      </p>
      {plan.notes.map((note) => (
        <p key={note} className="cfg-help">{note}</p>
      ))}
      {plan.owners.map((owner) => (
        <div key={owner.owner.key} className="routes-plan-owner" data-blocked={owner.blocked ? 'yes' : undefined}>
          <div className="routes-plan-head">
            <strong>{owner.owner.label}</strong>
            <span className="dim">{storageLabel(owner.storage)}</span>
          </div>
          {owner.blocked ? <div className="routes-message" data-tone="danger">Skipped: {owner.blocked}</div> : null}
          <table className="table routes-diff">
            <thead>
              <tr>
                <th>List</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {owner.lists.map((list) => (
                <tr key={`${list.slot}|${list.activity ?? ''}|${list.section ?? ''}`}>
                  <td>
                    {list.slotLabel}
                    {list.activity ? <span className="dim"> · {list.activity}</span> : null}
                    {list.section ? <span className="dim"> · on {list.section}</span> : null}
                  </td>
                  <td className="mono routes-before"><RouteList rows={list.before} /></td>
                  <td className="mono routes-after"><RouteList rows={list.after} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {owner.warnings.length > 0 ? (
            <ul className="routes-warnings">
              {owner.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/* ── the table ───────────────────────────────────────────────────────────── */

function RoutesTable({ inventory, filter, onFilter }: { inventory: UiRouteInventory; filter: RoutesFilter; onFilter: (next: RoutesFilter) => void }) {
  const options = useMemo(() => filterOptions(inventory), [inventory]);
  const visible = useMemo(() => filterRoutes(inventory.entries, filter), [inventory, filter]);
  const groups = useMemo(() => groupRoutes(visible), [visible]);
  const patch = (next: Partial<RoutesFilter>) => onFilter({ ...filter, ...next });
  const select = (label: string, value: string, key: keyof RoutesFilter, choices: Array<{ value: string; label: string }>, all: string) => (
    <label className="picker topbar-filter">
      <span className="topbar-filter-label">{label}</span>
      <select value={value} aria-label={`Filter by ${label.toLowerCase()}`} onChange={(event) => patch({ [key]: event.target.value })}>
        <option value="">{all}</option>
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>{choice.label}</option>
        ))}
      </select>
    </label>
  );

  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker count={visible.length}>Routes</Kicker>
      </header>
      <div className="sheet-bar routes-filters">
        {select('Project', filter.project, 'project', [{ value: 'nightshift', label: 'Nightshift-wide' }, ...inventory.projects.map((project) => ({ value: project.slug, label: project.name }))], 'All')}
        {select('Stage', filter.stage, 'stage', options.stages.map((stage) => ({ value: stage, label: stage })), 'All')}
        {select('Harness', filter.harness, 'harness', options.harnesses.map((harness) => ({ value: harness, label: harnessLabel(harness) })), 'All')}
        {select('Model', filter.model, 'model', options.models.map((model) => ({ value: model, label: model })), 'All')}
        {select('Machine', filter.machine, 'machine', [{ value: ANY_MACHINE, label: 'Any machine' }, ...options.machines.map((machine) => ({ value: machine, label: machine }))], 'All')}
        <label className="routes-check">
          <input type="checkbox" checked={filter.explicitOnly} onChange={(event) => patch({ explicitOnly: event.target.checked })} />
          Stored only
        </label>
        <label className="routes-check">
          <input type="checkbox" checked={filter.problemsOnly} onChange={(event) => patch({ problemsOnly: event.target.checked })} />
          Flagged only
        </label>
        {filterActive(filter) ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onFilter(EMPTY_ROUTES_FILTER)}>
            Clear filters
          </button>
        ) : null}
      </div>
      <div className="sheet-panel-body routes-scroll">
        {groups.length === 0 ? (
          <p className="cfg-help">No route matches these filters.</p>
        ) : (
          <table className="table routes-table-all">
            <thead>
              <tr>
                <th>List</th>
                <th className="right">#</th>
                <th>Machine</th>
                <th>Harness</th>
                <th>Model</th>
                <th>Thinking</th>
                <th>Status</th>
                <th>Source</th>
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.owner.key}>
                <tr className="routes-group">
                  <th colSpan={8} scope="rowgroup">
                    <span>{group.owner.label}</span>
                    <span className="dim"> · {groupNote(group)}</span>
                  </th>
                </tr>
                {group.entries.map((entry) => {
                  const status = routeStatus(entry);
                  return (
                    <tr key={entry.id} data-tone={status.tone === 'ok' ? undefined : status.tone} data-inherited={entry.explicit ? undefined : 'yes'}>
                      <td>
                        {entry.slotLabel}
                        {entry.activity ? <span className="dim"> · {entry.activity}</span> : null}
                        {entry.section ? <span className="dim"> · on {entry.section}</span> : null}
                      </td>
                      <td className="right">{entry.position}</td>
                      <td className="mono">{entry.machine ?? <span className="dim">any</span>}</td>
                      <td>{harnessLabel(entry.harness)}</td>
                      <td className="mono">{entry.model}</td>
                      <td>{entry.effort ?? <span className="dim">none</span>}</td>
                      <td>
                        <StatusChip entry={entry} />
                        {entry.problems.length > 0 ? <div className="routes-problems">{entry.problems.join(' · ')}</div> : null}
                      </td>
                      <td className="dim">{entry.explicit ? storageLabel(entry.storage) : `Inherits ${entry.inheritedFrom ?? ''}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        )}
      </div>
    </section>
  );
}

function RecentChanges({ inventory, now }: { inventory: UiRouteInventory; now: number }) {
  if (inventory.recentChanges.length === 0) return null;
  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker count={inventory.recentChanges.length}>Recent route changes</Kicker>
      </header>
      <div className="sheet-panel-body routes-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Owner</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {inventory.recentChanges.map((row) => (
              <tr key={row.id}>
                <td><Since at={row.createdAt} now={now} /></td>
                <td>{row.actor ?? <span className="dim">removed user</span>}</td>
                <td>{row.ownerLabel}</td>
                <td>{row.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
